import { mediaTypeOfUrl, isRenderableMediaUrl } from '../../screens/app/createScreen/utils/parameterUtils';

// Extract all media (inputs, outputs, prompts) from job data
export const extractMediaFromJob = (data) => {
  if (!data) return { inputs: [], outputs: [], prompts: {} };
  
  const inputs = [];
  const outputs = [];
  const prompts = {};
  
  // Determine if it's an image job or workflow job
  const isImageJob = !!data.image_job_id;
  const isWorkflowJob = !!data.workflow_job_id;
  
  // Extract outputs
  if (isImageJob && data.results) {
    data.results.forEach((result, index) => {
      if (!result) return; // Skip null results
      
      if (result.content_type === 'text') {
        prompts[`output_${index}`] = {
          type: 'output',
          label: `Generated Text ${index + 1}`,
          content: result.content
        };
      } else {
        outputs.push({
          id: result.image_id || `output_${index}`,
          url: result.content || result.external_url,
          type: result.content_type || 'image',
          label: `Output ${index + 1}`,
          metadata: result.additional_params
        });
      }
    });
  } else if (isWorkflowJob && data.results?.results) {
    data.results.results.forEach((result, index) => {
      if (!result) return; // Skip null results
      
      if (result.content_type === 'text') {
        prompts[`output_${index}`] = {
          type: 'output',
          label: `Generated Text ${index + 1}`,
          content: result.content
        };
      } else {
        outputs.push({
          id: result.image_id || `output_${index}`,
          url: result.content || result.external_url,
          type: result.content_type || 'image',
          label: `Output ${index + 1}`,
          metadata: result.additional_params
        });
      }
    });
  }
  
  // Extract input images - handle various possible keys
  const inputParams = data.input_params || {};
  const inputImages = data.input_images || {};
  
  // Collect all possible image URLs from input_params
  Object.entries(inputParams).forEach(([key, value]) => {
    if (typeof value === 'string' && (
      value.startsWith('http') || 
      value.startsWith('data:') ||
      key.includes('image') || 
      key.includes('url') ||
      key.includes('img')
    )) {
      // Shared gate + shared type detection, so this can't drift from
      // JobDetailView again. `type` follows the URL instead of always
      // claiming 'image'.
      if (isRenderableMediaUrl(value) || value.includes('image')) {
        inputs.push({
          id: `input_${key}`,
          url: value,
          type: mediaTypeOfUrl(value),
          label: formatLabel(key),
          source: 'params'
        });
      }
    }
  });
  
  // Also check input_images object
  Object.entries(inputImages).forEach(([key, value]) => {
    if (value && typeof value === 'string' && !inputs.find(i => i.url === value)) {
      inputs.push({
        id: `input_${key}`,
        url: value,
        type: mediaTypeOfUrl(value),
        label: formatLabel(key),
        source: 'images'
      });
    } else if (Array.isArray(value)) {
      value.forEach((url, idx) => {
        if (url && !inputs.find(i => i.url === url)) {
          inputs.push({
            id: `input_${key}_${idx}`,
            url: url,
            type: mediaTypeOfUrl(url),
            label: `${formatLabel(key)} ${idx + 1}`,
            source: 'images'
          });
        }
      });
    }
  });
  
  // Extract prompts - prioritize input_params over input_texts
  Object.entries(inputParams).forEach(([key, value]) => {
    if (typeof value === 'string' && (
      key.includes('prompt') || 
      key.includes('text') ||
      key === 'input' ||
      key === 'query'
    ) && !value.startsWith('http')) {
      prompts[key] = {
        type: 'input',
        label: formatLabel(key),
        content: value
      };
    }
  });
  
  // Only use input_texts if no prompts found in input_params
  if (Object.keys(prompts).length === 0) {
    const inputTexts = data.input_texts || {};
    Object.entries(inputTexts).forEach(([key, value]) => {
      if (value) {
        prompts[key] = {
          type: 'input',
          label: formatLabel(key),
          content: value
        };
      }
    });
  }
  
  return { inputs, outputs, prompts };
};

// Extract prompts from job data (separate function for convenience)
export const extractPromptsFromJob = (data) => {
  const { prompts } = extractMediaFromJob(data);
  return prompts;
};

// Format parameter key to human-readable label
export const formatLabel = (key) => {
  return key
    .replace(/_/g, ' ')
    .replace(/url/gi, '')
    .replace(/image/gi, 'Image')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Download media files
export const downloadMedia = async (mediaItems) => {
  if (mediaItems.length === 0) {
    return { success: 0, failed: 0 };
  }

  // If only one file, download directly
  if (mediaItems.length === 1) {
    return await downloadSingleFile(mediaItems[0]);
  }

  // If multiple files, create a zip
  return await downloadAsZip(mediaItems);
};

// Download a single file
const downloadSingleFile = async (item) => {
  try {
    // Try fetching with credentials: 'omit' first
    const response = await fetch(item.url, { 
      credentials: 'omit',
      mode: 'cors'
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    
    const blob = await response.blob();
    const ext = getFileExtension(blob.type, item.url);
    const filename = item.id ? `${item.id}.${ext}` : `download_${Date.now()}.${ext}`;
    
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    return { success: 1, failed: 0 };
  } catch (error) {
    console.error('Fetch download failed, trying direct link:', error);
    
    // Fallback: Open direct link (works for CORS-blocked resources)
    try {
      const ext = getFileExtension('', item.url);
      const filename = item.id ? `${item.id}.${ext}` : `download_${Date.now()}.${ext}`;
      
      const a = document.createElement('a');
      a.href = item.url;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      return { success: 1, failed: 0 };
    } catch (fallbackError) {
      console.error('Direct link download also failed:', fallbackError);
      return { success: 0, failed: 1 };
    }
  }
};

// Download multiple files as a zip
const downloadAsZip = async (mediaItems) => {
  try {
    const JSZip = (await import('jszip')).default;
    const zip = new JSZip();
    
    let success = 0;
    let failed = 0;
    let corsBlocked = [];
    
    // Fetch all files and add to zip
    for (const item of mediaItems) {
      try {
        const response = await fetch(item.url, { 
          credentials: 'omit',
          mode: 'cors'
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        
        const blob = await response.blob();
        const ext = getFileExtension(blob.type, item.url);
        const filename = item.id ? `${item.id}.${ext}` : `file_${success + 1}.${ext}`;
        
        zip.file(filename, blob);
        success++;
      } catch (error) {
        console.error(`Failed to fetch ${item.url}:`, error);
        // Check if it's a CORS error
        if (error.message.includes('CORS') || error.message.includes('fetch')) {
          corsBlocked.push(item);
        }
        failed++;
      }
    }
    
    // If we have some successful downloads, create zip
    if (success > 0) {
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const zipFilename = `outputs_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')}.zip`;
      
      const objectUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = zipFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }
    
    // If all files were CORS-blocked, fall back to individual downloads
    if (success === 0 && corsBlocked.length > 0) {
      console.log('All files CORS-blocked, falling back to individual downloads');
      return await downloadIndividualFiles(mediaItems);
    }
    
    // If some files were CORS-blocked, download them individually
    if (corsBlocked.length > 0) {
      console.log(`${corsBlocked.length} files were CORS-blocked, downloading individually`);
      const fallbackResult = await downloadIndividualFiles(corsBlocked);
      return { 
        success: success + fallbackResult.success, 
        failed: failed - corsBlocked.length + fallbackResult.failed 
      };
    }
    
    return { success, failed };
  } catch (error) {
    console.error('Zip creation failed:', error);
    // Fallback to individual downloads
    console.log('Zip failed, falling back to individual downloads');
    return await downloadIndividualFiles(mediaItems);
  }
};

// Fallback function to download files individually (for CORS issues)
const downloadIndividualFiles = async (mediaItems) => {
  let success = 0;
  let failed = 0;
  
  for (const item of mediaItems) {
    try {
      const ext = getFileExtension('', item.url);
      const filename = item.id ? `${item.id}.${ext}` : `download_${Date.now()}.${ext}`;
      
      const a = document.createElement('a');
      a.href = item.url;
      a.download = filename;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      success++;
      // Small delay between downloads to avoid browser blocking
      await new Promise(resolve => setTimeout(resolve, 100));
    } catch (error) {
      console.error(`Failed to download ${item.url}:`, error);
      failed++;
    }
  }
  
  return { success, failed };
};

// Get file extension from MIME type or URL
const getFileExtension = (mimeType, url) => {
  if (mimeType && mimeType.includes('/')) {
    const ext = mimeType.split('/')[1];
    return ext === 'jpeg' ? 'jpg' : ext;
  }
  
  const match = url.match(/\.([a-z0-9]+)(?:[?#]|$)/i);
  return match ? match[1] : 'jpg';
};

// Copy text to clipboard
export const copyToClipboard = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    return true;
  } catch (error) {
    console.error('Copy failed:', error);
    return false;
  }
};

// Get status color and icon
export const getStatusInfo = (status) => {
  switch (status) {
    case 'completed':
      return { color: '#52c41a', icon: '✅', label: 'Completed' };
    case 'processing':
      return { color: '#1890ff', icon: '⚡', label: 'Processing' };
    case 'pending':
      return { color: '#faad14', icon: '⏳', label: 'Pending' };
    case 'failed':
      return { color: '#ff4d4f', icon: '❌', label: 'Failed' };
    default:
      return { color: '#8c8c8c', icon: '•', label: status };
  }
};
